import { useEffect, useState } from 'react'
import {
  AdManagementService,
  type AspectRatio,
  type BrandPreset,
  type GeneratedAdResponse,
  type ReferenceImage,
  type StartGenerationResponse,
} from '../../client'
import { apiErrorMessage } from '../../lib/api'
import posthog from '../../lib/posthog'
import { posthogLogs } from '../../lib/posthogLogs'
import DialogMessage from '../ui/DialogMessage'
import Modal from '../ui/Modal'
import MediaPreview from '../home/MediaPreview'
import AdFieldsLoader from './AdFieldsLoader'
import AdPromptInput, { type Resolution } from './AdPromptInput'
import ReferenceImageManager from './ReferenceImageManager'

type Phase = 'scraping' | 'no_website' | 'config' | 'generating' | 'credits_exhausted' | 'error'

export type GenerationStarted = {
  ads: GeneratedAdResponse[]
  adIndices: number[]
  creditsRemaining: number
  creditsLimit: number
}

type Props = {
  brandId: number
  onClose: () => void
  onGenerated: (payload: GenerationStarted) => void
}

export default function GenerateAdsDialog({ brandId, onClose, onGenerated }: Props) {
  const [phase, setPhase] = useState<Phase>('scraping')
  const [presets, setPresets] = useState<BrandPreset[]>([])
  const [images, setImages] = useState<ReferenceImage[]>([])
  const [selectedUrls, setSelectedUrls] = useState<string[]>([])
  const [website, setWebsite] = useState('')
  const [websiteInput, setWebsiteInput] = useState('')
  const [prompt, setPrompt] = useState('')
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1')
  const [resolution, setResolution] = useState<Resolution>('1k')
  const [versions, setVersions] = useState(1)
  const [credits, setCredits] = useState({ consumed: 0, limit: 0, remaining: 0 })
  const [preview, setPreview] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)

  function applyStart(result: StartGenerationResponse, websiteUrl?: string) {
    setImages((result.reference_images ?? []).slice().reverse())
    setPresets(result.presets ?? [])
    setWebsite(result.website_url ?? websiteUrl ?? '')
    setPhase('config')
  }

  function handleStartError(err: unknown) {
    const detail = apiErrorMessage(err)
    if (detail.toLowerCase().includes('no website')) {
      setPhase('no_website')
      return
    }
    setMessage(detail)
    setPhase('error')
  }

  async function start(websiteUrl: string) {
    setPhase('scraping')
    setMessage('')

    try {
      applyStart(await AdManagementService.startAdGeneration(brandId, websiteUrl), websiteUrl)
    } catch (err) {
      handleStartError(err)
    }
  }

  useEffect(() => {
    AdManagementService.credits(brandId).then(setCredits).catch(() => undefined)
    AdManagementService.startAdGeneration(brandId).then((result) => applyStart(result)).catch(handleStartError)
  }, [brandId])

  async function submit() {
    if (submitting || !prompt.trim()) return
    if (versions > credits.remaining) {
      setMessage(`Not enough credits. You need ${versions} but have ${credits.remaining}.`)
      return
    }

    setSubmitting(true)
    setPhase('generating')

    try {
      const response = await AdManagementService.triggerAdGeneration(brandId, {
        prompt,
        num_versions: versions,
        aspect_ratio: aspectRatio,
        resolution,
        reference_image_urls: selectedUrls,
      })

      if (response.credits_exhausted) {
        posthogLogs.warn('ad_generation_not_started', { reason: 'credits_exhausted' })
        setPhase('credits_exhausted')
        return
      }
      if (response.success === false) {
        posthogLogs.error('ad_generation_not_started', { reason: 'request_rejected' })
        setMessage(response.error_message ?? 'Failed to generate ads.')
        setPhase('error')
        return
      }

      const startIndex = response.ad_start_index ?? 0
      posthog.capture('ad_generation_started', {
        aspect_ratio: aspectRatio,
        resolution,
        versions,
      })
      posthogLogs.info('ad_generation_started', {
        aspect_ratio: aspectRatio,
        resolution,
        versions,
      })
      onGenerated({
        ads: response.ads ?? [],
        adIndices: (response.ads ?? []).map((_, position) => startIndex + position),
        creditsRemaining: Math.max(0, credits.remaining - versions),
        creditsLimit: credits.limit,
      })
    } catch (err) {
      posthogLogs.error('ad_generation_not_started', { reason: 'request_failed' })
      setMessage(apiErrorMessage(err))
      setPhase('error')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Modal
        title="Generate Ads with AI"
        width="max-w-3xl"
        padded={false}
        onClose={onClose}
        headerRight={
          credits.limit > 0 && (
            <span className="text-xs text-gray-400">
              {credits.consumed} / {credits.limit} credits used
            </span>
          )
        }
      >
        {phase === 'scraping' ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 py-16">
            <div className="preloader mb-6" aria-hidden>
              {[0, 1, 2, 3, 4, 5].map((slice) => (
                <span key={slice} />
              ))}
            </div>
            <p className="text-lg font-semibold text-gray-800">Analyzing your brand website...</p>
            <p className="mt-1 text-sm text-gray-400">This usually takes 10-20 seconds</p>
          </div>
        ) : phase === 'no_website' ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center">
            <p className="text-base font-semibold text-gray-800">What is your website?</p>
            <p className="max-w-sm text-sm text-gray-500">
              Please enter your website URL to generate ad content tailored to your brand.
            </p>
            <div className="mt-2 flex w-full max-w-sm gap-2">
              <input
                autoFocus
                value={websiteInput}
                onChange={(event) => setWebsiteInput(event.target.value)}
                placeholder="yourbrand.com"
                className="h-10 flex-1 rounded-lg border border-gray-300 px-3 text-sm focus:border-purple-400 focus:outline-none"
              />
              <button
                type="button"
                disabled={!websiteInput.trim()}
                onClick={() => void start(websiteInput.trim())}
                className="rounded bg-purple-900 px-4 text-sm font-medium text-white transition hover:bg-purple-950 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        ) : phase === 'generating' ? (
          <div className="relative h-[520px] overflow-hidden">
            <AdFieldsLoader variant="generate-ads" isActive />
          </div>
        ) : phase === 'credits_exhausted' || phase === 'error' ? (
          <DialogMessage
            variant={phase === 'error' ? 'error' : 'credits'}
            message={phase === 'error' ? message : 'Credits reset at the start of your next billing cycle.'}
            onClose={onClose}
          />
        ) : (
          <>
            <div className="flex-1 space-y-6 overflow-y-auto px-7 py-6 [scrollbar-width:thin]">
              <AdPromptInput
                prompt={prompt}
                onPromptChange={setPrompt}
                aspectRatio={aspectRatio}
                onAspectRatioChange={setAspectRatio}
                resolution={resolution}
                onResolutionChange={setResolution}
                versions={versions}
                onVersionsChange={setVersions}
                creditsRemaining={credits.remaining}
                presets={presets}
                selectedImageUrls={selectedUrls}
                placeholder="Describe the ad you want... e.g., 'Minimalist black and white, focus on product details, elegant typography'"
              />

              <ReferenceImageManager
                brandId={brandId}
                images={images}
                selected={selectedUrls}
                websiteUrl={website}
                onImagesChange={setImages}
                onSelectedChange={setSelectedUrls}
                onPreview={setPreview}
              />

              {message && <p className="text-xs text-red-500">{message}</p>}
            </div>

            <div className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-100 px-7 py-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded bg-white px-4 py-2 text-sm font-medium text-slate-600 shadow-sm ring-1 ring-slate-300 transition ring-inset hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!prompt.trim() || submitting}
                onClick={() => void submit()}
                className="rounded bg-purple-900 px-5 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-purple-950 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? 'Starting...' : `Generate ${versions} Ad${versions > 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}
      </Modal>

      <MediaPreview src={preview} onClose={() => setPreview(null)} />
    </>
  )
}
